import { Component, Input, LOCALE_ID, OnChanges } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LocationListModel } from '../../models/location-list.model';
import { PropertyListModel } from '../../models/property-list.model';
import { SearchFilterModel } from '../../models/search-filter.model';
import { MapService } from '../../services/map.service';
import { SettingsService } from '../../services/settings.service';
import { TranslationPipe } from '../../pipes/translation.pipe';

import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { SearchCriteriaModel } from '../../models/search-criteria.model';
import { SearchService } from '../../services/search.service';
import { SettingsModel } from '../../models/setting.model';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-search-filter',
  templateUrl: './search-filter.component.html',
  styleUrls: ['./search-filter.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'no-no' },
    { provide: LOCALE_ID, useValue: 'no-no' },
  ],
  imports: [
    TranslationPipe,
    MatTooltipModule,
    MatRadioModule,
    MatFormFieldModule,
    FormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    CommonModule,
  ],
  standalone: true,
})
export class SearchFilterComponent implements OnChanges {
  @Input() properties: PropertyListModel[] = [];
  locations: LocationListModel[] = [];
  pois: LocationListModel[] = [];
  propertyTypes: SearchFilterModel[] = [];
  facilities: SearchFilterModel[] = [];
  settings: SettingsModel = new SettingsModel();

  @Input() currentSearchFilter: number = 0;
  @Input() currentRatingFilter: number = 0;
  selectedPropertyTypes: number[] = [];
  selectedFacilities: number[] = [];

  selectedSearchFilters: number[] = [];

  searchFilters: SearchFilterModel[] = [];

  rating: number = 0;
  customerRating: number = 0;
  location: string = '';
  activeLocation: string = '';
  poi: string = '';
  activePoi: string = '';

  propertyTypeFormControl = new FormControl();
  facilityFormControl = new FormControl();
  searchFilterFormControl = new FormControl();

  showFilter = false;

  searchCriteria: SearchCriteriaModel = new SearchCriteriaModel();

  constructor(
    private mapService: MapService,
    private router: Router,
    private settingsService: SettingsService,
    private searchService: SearchService,
  ) {}

  ngOnChanges(): void {
    this.settings = this.settingsService.settings();
    if (this.settings && this.settings.LandingPages) {
      this.locations = this.settings.LandingPages.filter((x) => x.Type === 1);
      this.pois = this.settings.LandingPages.filter((x) => x.Type === 2);
      this.searchFilters = this.settings.SearchFilters;

      if (this.location) {
        this.activeLocation =
          this.locations.find((x) => x.Url === this.location)?.Name ?? '';
      }

      if (this.poi) {
        this.activePoi = this.pois.find((x) => x.Url === this.poi)?.Name ?? '';
      }
    }

    this.location = this.router.url.split('?')[0].substring(1);
    this.poi = this.router.url.split('?')[0].substring(1);

    this.searchCriteria = this.searchService.clientSearchCriteria();

    this.rating = this.searchCriteria.RatingMin;
    this.customerRating = this.searchCriteria.CustomerRatingMin;
    this.selectedSearchFilters = this.searchCriteria.SearchFilter;

    if (this.selectedSearchFilters.length > 0) {
      this.selectedSearchFilters.forEach((el) => {
        let searchFilter = this.searchFilters.find((x) => x.Id === el);

        if (searchFilter) {
          searchFilter.Selected = true;
        }
      });
    }

    if (this.currentRatingFilter > 0) {
      this.rating = this.currentRatingFilter;
    }
  }

  toggleShowFilter() {
    this.showFilter = !this.showFilter;
    return;
  }

  changeRatingFilter(value: number) {
    let searchCriteria = { ...this.searchService.clientSearchCriteria() };
    searchCriteria.RatingMin = value;
    this.searchService.setClientSearchCriteria(
      searchCriteria,
      'customerRating',
    );
  }

  changeCustomerRatingFilter(value: number) {
    let searchCriteria = { ...this.searchService.clientSearchCriteria() };
    this.customerRating = searchCriteria.CustomerRatingMin = value;
    this.searchService.setClientSearchCriteria(
      searchCriteria,
      'customerRating',
    );
  }

  changeLocation(url: string) {
    this.toggleShowFilter();
    this.router.navigateByUrl(url);
  }

  changePoi(url: string) {
    this.toggleShowFilter();
    this.router.navigateByUrl(url);
  }

  changeFacility(id: number, event: any) {
    let searchCriteria = { ...this.searchService.clientSearchCriteria() };
    if (event.checked) {
      searchCriteria.SearchFilter.push(id);
    } else {
      searchCriteria.SearchFilter = this.searchCriteria.SearchFilter.filter(
        (x) => x !== id,
      );
    }

    this.searchService.setClientSearchCriteria(searchCriteria, 'location');
  }

  changeSearchFilter(id: number, event: any) {
    let searchCriteria = { ...this.searchService.clientSearchCriteria() };
    if (event.checked) {
      searchCriteria.SearchFilter.push(id);
    } else {
      searchCriteria.SearchFilter = this.searchCriteria.SearchFilter.filter(
        (x) => x !== id,
      );
    }

    this.searchService.setClientSearchCriteria(searchCriteria, 'location');
  }

  addFacility(id: number) {
    this.selectedFacilities.push(id);
  }

  removeSearchFilter(id: number) {
    const currentSearchFilterIndex = this.selectedSearchFilters.findIndex(
      (y) => y === id,
    );

    this.selectedSearchFilters.splice(currentSearchFilterIndex, 1);

    this.searchCriteria.SearchFilter = this.selectedSearchFilters;

    const searchFilter = this.searchFilters.find((x) => x.Id === id);
    if (searchFilter) {
      searchFilter.Selected = false;
    }

    this.searchService.setClientSearchCriteria(this.searchCriteria, 'types');
  }

  getRating(rating: number) {
    let stars = '';
    for (let i = 0; i < rating; i++) {
      stars += '★';
    }

    return stars;
  }

  getCustomerRating(rating: number) {
    return rating + ' +';
  }

  getPropertyTypes() {
    return this.searchFilters.filter((x) => x.Type === 1);
  }

  getFacilities() {
    return this.searchFilters.filter((x) => x.Type === 2);
  }
}
